/**
 * derivedSigil.js — gestione client dei sigil derivati (Premium).
 *
 * Flow di creazione:
 *   1. l'utente sceglie un nome sigil + label locale ("amici", "lavoro" ...)
 *   2. la mnemonica master (in memoria post-unlock) deriva via HKDF una
 *      coppia X25519 + Ed25519 univoca per quel contesto
 *   3. le pubblice vengono inviate al server via `createDerivedSigil`
 *   4. il server registra `sigils/{lower}` con `derived:true` + ownerUid +
 *      pubkeys, e appende all'array `users/{uid}.derivedSigils[]`
 *
 * Le PRIVATE non lasciano mai il device. Stessa mnemonica + stessa label
 * → stesse private. Niente storage extra.
 */

import { callFn } from './firebase';
import { mnemonicToSeed } from './mnemonic';
import { deriveDerivedC2C } from './keyDerivation';
import { toBase64Url } from './crypto';

/**
 * Deriva sul device le chiavi pubbliche del sigil derivato e le invia al
 * server per la registrazione. Idempotente solo se sigil libero.
 *
 * @param {object} p
 * @param {string} p.mnemonic     - le 24 parole (gia' in memoria dopo unlock)
 * @param {string} p.sigil        - es. "vento_libero" (3-20 chars [a-zA-Z0-9_])
 * @param {string} p.label        - etichetta locale, es. "amici"
 * @returns {Promise<{ok:true, sigil:string, pubKeyBox:string, pubKeySign:string}>}
 */
export async function createDerivedSigil({ mnemonic, sigil, label }) {
  if (!mnemonic) throw new Error('mnemonic non in memoria');
  if (!sigil || !/^[a-zA-Z0-9_]{3,20}$/.test(sigil)) throw new Error('sigil non valido (3-20 chars, [a-zA-Z0-9_])');

  const seed = mnemonicToSeed(mnemonic);
  // contesto della derivazione = sigil stesso (case-insensitive normalizzato)
  // così la stessa label → stessa derivazione → stesse chiavi → recuperabili
  // su qualsiasi device che abbia la mnemonica.
  const keys = await deriveDerivedC2C(seed, sigil.toLowerCase());
  const pubKeyBox = await toBase64Url(keys.box.publicKey);
  const pubKeySign = await toBase64Url(keys.sign.publicKey);

  const res = await callFn('createDerivedSigil')({
    sigil,
    pubKeyBox,
    pubKeySign,
    labelLocal: label || '',
  });
  const d = res?.data;
  if (!d?.ok) throw new Error(d?.error || 'create failed');
  return { ok: true, sigil: d.sigil, pubKeyBox, pubKeySign };
}

/**
 * Ricarica la lista derivati dell'utente dal server (per la UI premium).
 */
export async function listMyDerivedSigils() {
  const res = await callFn('listMyDerivedSigils')({});
  const d = res?.data;
  if (!d?.ok) return [];
  return d.derivedSigils || [];
}

/**
 * Tomba un sigil derivato (server-side): il sigil sparisce dalla mappa
 * pubblica e l'invio a quel nome restituira' "destinatario non esistente".
 */
export async function tombDerivedSigil(sigil) {
  if (!sigil) throw new Error('sigil mancante');
  const res = await callFn('tombDerivedSigil')({ sigil });
  const d = res?.data;
  if (!d?.ok) throw new Error(d?.error || 'tomb failed');
  return { ok: true };
}

/**
 * Stato premium + lista derivati in una sola chiamata.
 */
export async function getMyAccount() {
  const res = await callFn('getMyAccount')({});
  const d = res?.data;
  const empty = {
    premium: false, premiumStatus: 'none', premiumExpiresAt: null,
    premiumDaysLeft: null, premiumGraceDaysLeft: null, betaTester: false,
    tosVersion: null,
    role: 'member', canInvite: true, inviteLimit: null,
    inviteUsedThisMonth: 0, inviteRemaining: null,
    identityMode: 'open', masterFindable: true, derivedSigils: [],
  };
  if (!d?.ok) return empty;
  return {
    premium: !!d.premium,
    premiumStatus: d.premiumStatus || (d.premium ? 'lifetime' : 'none'),
    premiumExpiresAt: d.premiumExpiresAt ?? null,
    premiumDaysLeft: d.premiumDaysLeft ?? null,
    premiumGraceDaysLeft: d.premiumGraceDaysLeft ?? null,
    betaTester: d.betaTester === true,
    tosVersion: d.tosVersion ?? null,
    // Ruolo crescita beta + quota inviti del mese corrente.
    role: d.role || 'member',
    canInvite: d.canInvite !== false,
    inviteLimit: d.inviteLimit ?? null,
    inviteUsedThisMonth: d.inviteUsedThisMonth ?? 0,
    inviteRemaining: d.inviteRemaining ?? null,
    identityMode: d.identityMode || 'open',
    masterFindable: d.masterFindable !== false,
    derivedSigils: d.derivedSigils || [],
  };
}

/** Imposta la modalità identità: 'open' | 'reserved' | 'hybrid'. */
export async function setIdentityMode(mode) {
  const res = await callFn('setIdentityMode')({ mode });
  const d = res?.data;
  if (!d?.ok) throw new Error(d?.error || 'set mode failed');
  return d;
}

/** Accende/spegne la ricercabilità di un singolo sigil (master o derivato). */
export async function setSigilFindable(sigil, findable) {
  const res = await callFn('setSigilFindable')({ sigil, findable });
  const d = res?.data;
  if (!d?.ok) throw new Error(d?.error || 'set findable failed');
  return d;
}

/**
 * Helper: deriva localmente le chiavi private di un sigil derivato (per
 * decifrare messaggi ricevuti su quel sigil).
 */
export async function localKeysForDerivedSigil({ mnemonic, sigilLower }) {
  if (!mnemonic) throw new Error('mnemonic non in memoria');
  const seed = mnemonicToSeed(mnemonic);
  const keys = await deriveDerivedC2C(seed, sigilLower);
  return {
    boxPub: keys.box.publicKey,
    boxSec: keys.box.privateKey,
    signPub: keys.sign.publicKey,
    signSec: keys.sign.privateKey,
  };
}
